import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Stamp, CheckCircle2, Package, Truck, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast, Toaster } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ESTADO_META = {
  solicitado: { label: 'Solicitado — esperando validación en el local', color: 'bg-slate-100 text-slate-700' },
  pago_pendiente_colegio: { label: 'Validado — esperando pago del Colegio', color: 'bg-amber-100 text-amber-800' },
  pagado: { label: 'Pagado — pendiente de fabricación', color: 'bg-blue-100 text-blue-800' },
  en_produccion: { label: 'En fabricación', color: 'bg-purple-100 text-purple-800' },
  listo_retiro: { label: 'Listo para retirar', color: 'bg-green-100 text-green-800' },
  entregado: { label: 'Entregado', color: 'bg-slate-200 text-slate-700' },
};

const ACCION_LABEL = {
  pago_pendiente_colegio: { text: 'Solicitar Fabricación', icon: Stamp, color: 'bg-amber-600 hover:bg-amber-700' },
  en_produccion: { text: 'Iniciar Fabricación', icon: Package, color: 'bg-purple-600 hover:bg-purple-700' },
  listo_retiro: { text: 'Marcar como Listo para Retirar', icon: Truck, color: 'bg-green-600 hover:bg-green-700' },
  entregado: { text: 'Marcar como Entregado', icon: CheckCircle2, color: 'bg-slate-700 hover:bg-slate-800' },
};

export default function SelloProveedorPage() {
  const { token: accessToken } = useParams();
  const [loading, setLoading] = useState(true);
  const [avanzando, setAvanzando] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [pin, setPin] = useState('');

  const cargar = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API_URL}/sellos/publico/${accessToken}`);
      setData(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo cargar el pedido');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [accessToken]);

  const avanzar = async () => {
    if (!data?.siguiente_estado) return;
    if (data.requiere_pin && (!pin || pin.trim().length < 4)) {
      toast.error('Ingresá el PIN de confirmación');
      return;
    }
    if (!window.confirm(`¿Confirmás avanzar a "${ESTADO_META[data.siguiente_estado]?.label}"?`)) return;
    setAvanzando(true);
    try {
      await axios.post(
        `${API_URL}/sellos/publico/${accessToken}/avanzar`,
        { nuevo_estado: data.siguiente_estado, pin: data.requiere_pin ? pin.trim() : undefined },
      );
      toast.success('Estado actualizado');
      setPin('');
      await cargar();
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    } finally {
      setAvanzando(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 gap-2">
        <Loader2 className="w-6 h-6 animate-spin" /> Buscando pedido…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-2"><XCircle className="w-12 h-12 text-red-500" /></div>
            <CardTitle>Pedido no encontrado</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const meta = ESTADO_META[data.estado] || ESTADO_META.solicitado;
  const accion = data.siguiente_estado ? ACCION_LABEL[data.siguiente_estado] : null;
  const AccionIcon = accion?.icon;

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Toaster position="top-center" />
      <Card className="max-w-lg w-full">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-2"><Stamp className="w-10 h-10 text-purple-700" /></div>
          <CardTitle className="text-xl">Sello Profesional</CardTitle>
          <CardDescription>Panel del proveedor</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="rounded-lg border p-4 bg-slate-50 space-y-2" data-testid="prov-datos-socio">
            <div className="text-xs text-slate-500 uppercase tracking-wide">Titular</div>
            <div className="text-2xl font-bold text-slate-900">
              {data.nombre} {data.apellido}
            </div>
            <div className="text-lg text-slate-700">
              Matrícula: <strong className="font-mono">{data.matricula}</strong>
            </div>
            {data.evento_nombre && (
              <div className="text-xs text-slate-500 pt-1 border-t border-slate-200 mt-2">
                Evento: {data.evento_nombre}
              </div>
            )}
          </div>

          <div className="flex justify-center" data-testid="prov-estado-badge">
            <Badge className={`text-base px-4 py-1.5 ${meta.color}`}>
              {meta.label}
            </Badge>
          </div>

          {data.estado === 'solicitado' && (
            <div className="text-sm text-slate-700 bg-slate-100 border border-slate-200 rounded p-3 text-center">
              El socio presentó su QR en el local. Validá el pedido presionando
              <strong> "Solicitar Fabricación"</strong> para habilitar el pago del Colegio.
            </div>
          )}

          {data.estado === 'pago_pendiente_colegio' && (
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3 text-center">
              Pedido validado. Esperando confirmación de pago del Colegio antes de fabricar.
            </div>
          )}

          {data.requiere_pin && (
            <div className="border border-purple-300 bg-purple-50 rounded p-3">
              <label className="text-sm font-medium text-purple-900 block mb-1">
                PIN de confirmación
              </label>
              <input
                type="password"
                inputMode="numeric"
                autoComplete="off"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="••••"
                maxLength={8}
                className="w-full border rounded px-3 py-2 text-lg tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-purple-400"
                data-testid="prov-input-pin"
              />
              <p className="text-xs text-purple-700 mt-1">
                Ingresá el PIN que te dio el Colegio para autorizar la solicitud de fabricación.
              </p>
            </div>
          )}

          {accion && (
            <Button
              size="lg"
              onClick={avanzar}
              disabled={avanzando || (data.requiere_pin && (pin || '').trim().length < 4)}
              className={`w-full text-white text-lg py-7 ${accion.color}`}
              data-testid="prov-btn-avanzar"
            >
              {avanzando
                ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Guardando…</>
                : <><AccionIcon className="w-6 h-6 mr-2" /> {accion.text}</>}
            </Button>
          )}

          {data.estado === 'entregado' && (
            <div className="text-center text-green-800 bg-green-50 border border-green-200 rounded p-4">
              <CheckCircle2 className="w-8 h-8 mx-auto mb-2" />
              Pedido finalizado. Gracias.
            </div>
          )}

          {data.proveedor && (
            <div className="text-xs text-slate-400 text-center pt-3 border-t">
              Proveedor asignado: {data.proveedor.nombre}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
